import type { ClubRole } from "@rivaled/shared";
import { createFileRoute, Link, Outlet, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { BarChart3, Gavel, ShieldCheck } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import { Breadcrumbs } from "../../../../../components/ui";
import { usePlatformRole } from "../../../../../hooks/usePlatformRole";
import { canClubRolePermission } from "../../../../../lib/permissions";
import { trpc } from "../../../../../lib/trpc";
import { cn } from "../../../../../lib/utils";

export const Route = createFileRoute("/app/clubs/$slug/admin")({
  component: ClubAdminLayout,
});

function ClubAdminLayout() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const redirectedRef = useRef(false);
  const { isLoading: isPlatformRoleLoading, isModerator: isPlatformModerator } = usePlatformRole();

  const { data: club, isLoading: clubLoading } = trpc.club.get.useQuery({ slug });
  const { data: myClubs, isLoading: clubsLoading } = trpc.club.getMyClubs.useQuery();

  const membership = myClubs?.find((c) => c.slug === slug);
  const canAccess =
    canClubRolePermission(membership?.role as ClubRole | undefined, {
      resource: "dispute",
      actions: ["view"],
    }) || isPlatformModerator;

  if (clubLoading || clubsLoading || isPlatformRoleLoading) {
    return (
      <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-6">
        <div className="h-8 w-56 rounded-lg bg-surface-700/40 animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-xl bg-surface-800/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!club || !canAccess) {
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      toast.error("Moderator access is required for club admin.");
      void navigate({ to: "/app/clubs/$slug", params: { slug } });
    }
    return null;
  }

  const isDisputes =
    !!matchRoute({ to: "/app/clubs/$slug/admin", params: { slug } }) ||
    !!matchRoute({ to: "/app/clubs/$slug/admin/disputes", params: { slug } });
  const isModeration = !!matchRoute({ to: "/app/clubs/$slug/admin/moderation", params: { slug } });
  const isAnalytics = !!matchRoute({ to: "/app/clubs/$slug/admin/analytics", params: { slug } });

  const navItems = [
    {
      id: "disputes",
      label: "Disputes",
      icon: <Gavel className="size-4" />,
      active: isDisputes,
    },
    {
      id: "moderation",
      label: "Moderation",
      icon: <ShieldCheck className="size-4" />,
      active: isModeration,
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: <BarChart3 className="size-4" />,
      active: isAnalytics,
    },
  ] as const;

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      <header className="space-y-3">
        <Breadcrumbs
          items={[
            { label: club.name, to: "/app/clubs/$slug", params: { slug } },
            { label: "Admin" },
          ]}
        />
        <h1 className="text-2xl font-bold text-white">Admin</h1>
      </header>

      <div className="lg:hidden overflow-x-auto scrollbar-none -mx-4 px-4">
        <div className="flex gap-1 min-w-max pb-1">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={`/app/clubs/$slug/admin/${item.id}` as const}
              params={{ slug }}
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors",
                item.active
                  ? "bg-brand-500/10 text-brand-400"
                  : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50",
              )}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="flex gap-6">
        <nav className="hidden lg:block w-55 shrink-0">
          <div className="sticky top-6 space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={`/app/clubs/$slug/admin/${item.id}` as const}
                params={{ slug }}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  item.active
                    ? "bg-brand-500/10 text-brand-400"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50",
                )}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
